import { useEffect, useState, createContext, useContext } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { getDashboardCharts } from "../api/admin";
import { ACCENT, pickBlueShades, PIE_COLORS, PIE_HOVER } from "../../src/utils/chartPalette";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
);

/* ── colour palette ─────────────────────────────────────── */
/* ── shared chart options ───────────────────────────────── */
const baseFont = {
  family: "'Inter','Segoe UI','Roboto',sans-serif",
  size: 11,
  weight: 500,
};

const isDarkAdminTheme = () => {
  if (typeof document === "undefined") return false;
  return document.documentElement?.dataset?.adminTheme === "dark";
};

const getLegendTextColor = () => {
  return isDarkAdminTheme() ? "#cbd5e1" : "#334155";
};

const getAxisTextColor = () => (isDarkAdminTheme() ? "#94a3b8" : "#64748b");
const getGridLineColor = () => (isDarkAdminTheme() ? "rgba(148,163,184,0.18)" : "rgba(0,0,0,0.06)");

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: { ...baseFont, color: () => getLegendTextColor(), boxWidth: 10, padding: 12 },
    },
    tooltip: { backgroundColor: "#0f172a", titleFont: { ...baseFont, weight: 700 }, bodyFont: baseFont, padding: 10, cornerRadius: 8 },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: baseFont, color: () => getAxisTextColor() } },
    y: { beginAtZero: true, grid: { color: () => getGridLineColor() }, ticks: { font: baseFont, color: () => getAxisTextColor(), precision: 0 } },
  },
};

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "x",
  plugins: {
    legend: { display: false },
    tooltip: { backgroundColor: "#0f172a", titleFont: { ...baseFont, weight: 700 }, bodyFont: baseFont, padding: 10, cornerRadius: 8 },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { ...baseFont, size: 10 }, color: () => getAxisTextColor() } },
    y: { beginAtZero: true, grid: { color: () => getGridLineColor() }, ticks: { font: baseFont, color: () => getAxisTextColor(), precision: 0 } },
  },
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "62%",
  radius: "92%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        ...baseFont,
        color: () => getLegendTextColor(),
        boxWidth: 10,
        padding: 8,
        font: { ...baseFont, size: 10 },
        generateLabels: (chart) => {
          const data = chart.data;
          if (!data.labels?.length) return [];
          return data.labels.map((label, i) => ({
            text: label,
            fillStyle: data.datasets[0].backgroundColor[i],
            strokeStyle: data.datasets[0].backgroundColor[i],
            fontColor: getLegendTextColor(),
            hidden: false,
            index: i,
          }));
        },
      },
    },
    tooltip: {
      backgroundColor: "#0f172a",
      titleFont: { ...baseFont, weight: 700 },
      bodyFont: baseFont,
      padding: 10,
      cornerRadius: 8,
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((s, v) => s + v, 0);
          const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
          return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
        },
      },
    },
  },
};

/* ── shared data context ────────────────────────────────── */
const ChartDataContext = createContext(null);

export function DashboardChartsProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getDashboardCharts();
        if (alive) setData(res.data);
      } catch (err) {
        if (alive) setError(err?.response?.data?.message ?? "Unable to load chart data.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  return (
    <ChartDataContext.Provider value={{ data, loading, error }}>
      {children}
    </ChartDataContext.Provider>
  );
}

function useChartData() {
  return useContext(ChartDataContext);
}

/* ── Line + Bar charts row (left side) ──────────────────── */
export function DashboardChartsRow() {
  const { data, loading, error } = useChartData();

  if (loading || !data) {
    return (
      <div className="dashboard-charts-row">
        {[1, 2].map((i) => (
          <div key={i} className="admin-surface dashboard-chart-card dashboard-chart-skeleton">
            <div className="dashboard-chart-skeleton__bar" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-charts-row">
        <div className="admin-surface dashboard-chart-card">
          <div className="admin-empty-state">{error}</div>
        </div>
      </div>
    );
  }

  const lineData = {
    labels: data.memberRegistrations.map((d) => d.label),
    datasets: [{
      label: "Members",
      data: data.memberRegistrations.map((d) => d.count),
      borderColor: ACCENT.blue,
      backgroundColor: ACCENT.blueSoft,
      pointBackgroundColor: ACCENT.blue,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.35,
      fill: true,
    }],
  };

  const departmentLabelMap = {
    "Legal Department": "Legal",
    "Medical Department": "Medical",
    "Education Department": "Education",
    "Emergency Support": "Emergency",
  };

  const barData = {
    labels: data.ticketsByDepartment.map((d) => departmentLabelMap[d.department] || d.department),
    datasets: [{
      label: "Tickets",
      data: data.ticketsByDepartment.map((d) => d.count),
      backgroundColor: pickBlueShades(data.ticketsByDepartment.length),
      borderRadius: 6,
      borderSkipped: false,
      maxBarThickness: 44,
    }],
  };

  return (
    <div className="dashboard-charts-row">
      <div className="admin-surface dashboard-chart-card">
        <div className="dashboard-chart-card__head">
          <h3 className="dashboard-chart-card__title">
            Membership Count
          </h3>
          <span className="admin-muted dashboard-chart-card__sub">
            Member count and new registrations for the past 6 months.
          </span>
        </div>
        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--line">
          <Line data={lineData} options={lineOptions} />
        </div>
      </div>

      <div className="admin-surface dashboard-chart-card">
        <div className="dashboard-chart-card__head">
          <h3 className="dashboard-chart-card__title">Tickets by Department</h3>
          <span className="admin-muted dashboard-chart-card__sub">
            Volume of support tickets per department.
          </span>
        </div>
        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--bar">
          <Bar data={barData} options={barOptions} />
        </div>
      </div>
    </div>
  );
}

/* ── Pie chart sidebar (right side) ─────────────────────── */
export function DashboardChartsSidebar() {
  const { data, loading, error } = useChartData();
  const [companyAffiliationIndex, setCompanyAffiliationIndex] = useState(0);

  if (loading || !data || error) {
    return (
      <aside className="dashboard-body__side">
        {[1, 2].map((i) => (
          <div key={i} className="admin-surface dashboard-chart-card dashboard-chart-skeleton">
            <div className="dashboard-chart-skeleton__bar" />
          </div>
        ))}
      </aside>
    );
  }

  const affiliationPie = {
    labels: data.membersByAffiliation.map((d) => d.affiliation),
    datasets: [{
      data: data.membersByAffiliation.map((d) => d.count),
      backgroundColor: pickBlueShades(data.membersByAffiliation.length, PIE_COLORS),
      hoverBackgroundColor: pickBlueShades(data.membersByAffiliation.length, PIE_HOVER),
      hoverOffset: 6,
      borderWidth: 2,
      borderColor: "#ffffff",
    }],
  };

  const companiesByAffiliation = Array.isArray(data.membersByCompanyByAffiliation)
    ? data.membersByCompanyByAffiliation
    : [];

  const boundedCompanyAffiliationIndex = companiesByAffiliation.length > 0
    ? Math.min(companyAffiliationIndex, companiesByAffiliation.length - 1)
    : 0;

  const activeCompanyAffiliation = companiesByAffiliation[boundedCompanyAffiliationIndex] || null;

  const rotateAffiliation = (delta) => {
    if (!companiesByAffiliation.length) return;
    setCompanyAffiliationIndex((prev) => {
      const next = prev + delta;
      const max = companiesByAffiliation.length;
      return ((next % max) + max) % max;
    });
  };

  const companyDistributionPie = {
    labels: activeCompanyAffiliation?.companies?.map((d) => d.company) || [],
    datasets: [{
      data: activeCompanyAffiliation?.companies?.map((d) => d.count) || [],
      backgroundColor: pickBlueShades(activeCompanyAffiliation?.companies?.length || 0),
      hoverBackgroundColor: pickBlueShades(activeCompanyAffiliation?.companies?.length || 0, PIE_HOVER),
      hoverOffset: 6,
      borderWidth: 2,
      borderColor: "#ffffff",
    }],
  };

  return (
    <aside className="dashboard-body__side">
      <div className="admin-surface dashboard-chart-card dashboard-chart-card--pie-sidebar">
        <div className="dashboard-chart-card__head">
          <h3 className="dashboard-chart-card__title">Member Distribution</h3>
          <span className="admin-muted dashboard-chart-card__sub">
            Breakdown by union affiliation.
          </span>
        </div>
        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--pie">
          <Doughnut data={affiliationPie} options={pieOptions} />
        </div>
      </div>

      <div className="admin-surface dashboard-chart-card dashboard-chart-card--pie-sidebar">
        <div className="dashboard-chart-card__head">
          <div className="dashboard-chart-card__head-row">
            <h3 className="dashboard-chart-card__title">Company Distribution</h3>
            <div className="dashboard-chart-switcher" aria-label="Change affiliation">
              <button
                type="button"
                className="dashboard-chart-switcher__btn"
                onClick={() => rotateAffiliation(-1)}
                disabled={!companiesByAffiliation.length}
                aria-label="Previous affiliation"
              >
                &lt;
              </button>
              <span className="dashboard-chart-switcher__label">
                {activeCompanyAffiliation?.affiliation || "Unspecified"}
              </span>
              <button
                type="button"
                className="dashboard-chart-switcher__btn"
                onClick={() => rotateAffiliation(1)}
                disabled={!companiesByAffiliation.length}
                aria-label="Next affiliation"
              >
                &gt;
              </button>
            </div>
          </div>
          <span className="admin-muted dashboard-chart-card__sub">
            Member distribution by company for selected affiliation.
          </span>
        </div>
        <div className="dashboard-chart-card__canvas dashboard-chart-card__canvas--pie">
          <Doughnut data={companyDistributionPie} options={pieOptions} />
        </div>
      </div>
    </aside>
  );
}

/* ── Default export (not used, kept for compat) ─────────── */
export default function DashboardCharts() {
  return null;
}
